/**
 * Planet surfaces — Mars, Venus, Saturn. Each has cool stuff, a rentable space
 * house (2500 VC / 7 days) and a hidden secret room behind a shimmering entrance.
 */
import { drawShip } from './moonZone';

// interactive zones shared by all planet surfaces (800x520 interior space)
export const PLANET_SPOTS = {
  ship: { x: 90, y: 445 },
  house: { x: 640, y: 300 },
  secret: {
    mars: { x: 300, y: 392 },    // cave in a crater
    venus: { x: 430, y: 340 },   // glowing door in the mist
    saturn: { x: 340, y: 408 },  // hatch in the ring-ice
  },
};

export const PLANET_HOUSE_NAMES = { mars: 'Red Dune Hab', venus: 'Cloud Nine Villa', saturn: 'Ring View Loft' };

function stars(ctx, t, n, yMax) {
  for (let i = 0; i < n; i++) {
    const sx = (i * 149) % 790, sy = 14 + ((i * 233) % yMax);
    ctx.fillStyle = `rgba(255,255,255,${0.25 + Math.abs(Math.sin(t * 0.05 + i)) * 0.6})`;
    ctx.fillRect(sx, sy, 1.6, 1.6);
  }
}

function shimmer(ctx, x, y, t, color) {
  const a = 0.25 + Math.abs(Math.sin(t * 0.09)) * 0.45;
  for (let i = 0; i < 5; i++) {
    const px = x - 22 + ((i * 13 + t * 0.7) % 44);
    const py = y - 26 + ((i * 19 + t * 0.5) % 40);
    ctx.fillStyle = color.replace('$A', String(a * (0.5 + (i % 3) * 0.25)));
    ctx.beginPath(); ctx.arc(px, py, 1.6 + (i % 2), 0, Math.PI * 2); ctx.fill();
  }
}

// floating twinkle particles across the whole room (deterministic drift)
function motes(ctx, t, n, color, yTop = 70, yBot = 500) {
  const span = yBot - yTop;
  for (let i = 0; i < n; i++) {
    const px = (i * 137 + t * (0.4 + (i % 3) * 0.2)) % 800;
    const py = yTop + ((i * 211 + t * (0.3 + (i % 4) * 0.15)) % span);
    const tw = 0.3 + Math.abs(Math.sin(t * 0.08 + i * 1.7)) * 0.7;
    ctx.fillStyle = color.replace('$A', String(tw * 0.7));
    ctx.beginPath(); ctx.arc(px, py, 0.8 + (i % 3) * 0.7, 0, Math.PI * 2); ctx.fill();
  }
}

// a faceted gem/crystal with glow, pointing up
function crystal(ctx, x, baseY, w, h, hue, glow) {
  ctx.save();
  ctx.shadowColor = hue; ctx.shadowBlur = 14 * glow;
  ctx.fillStyle = hue;
  ctx.globalAlpha = 0.45 + glow * 0.4;
  ctx.beginPath();
  ctx.moveTo(x, baseY - h);
  ctx.lineTo(x + w * 0.5, baseY - h * 0.55);
  ctx.lineTo(x + w * 0.42, baseY);
  ctx.lineTo(x - w * 0.42, baseY);
  ctx.lineTo(x - w * 0.5, baseY - h * 0.55);
  ctx.closePath(); ctx.fill();
  ctx.shadowBlur = 0;
  // facet highlight
  ctx.globalAlpha = 0.35 + glow * 0.3;
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.beginPath();
  ctx.moveTo(x, baseY - h);
  ctx.lineTo(x - w * 0.16, baseY - h * 0.35);
  ctx.lineTo(x, baseY);
  ctx.lineTo(x + w * 0.14, baseY - h * 0.4);
  ctx.closePath(); ctx.fill();
  ctx.restore();
}

function drawHouse(ctx, t, rental, style) {
  const { x, y } = PLANET_SPOTS.house;
  // dome hab
  ctx.fillStyle = style.wall;
  ctx.beginPath(); ctx.arc(x, y + 40, 92, Math.PI, 0); ctx.fill();
  ctx.strokeStyle = style.trim; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.arc(x, y + 40, 92, Math.PI, 0); ctx.stroke();
  ctx.fillStyle = 'rgba(125,211,252,0.25)';
  ctx.beginPath(); ctx.arc(x - 34, y + 6, 16, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + 38, y + 2, 12, 0, Math.PI * 2); ctx.fill();
  // door
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(x - 22, y + 4, 44, 76);
  ctx.strokeStyle = style.trim; ctx.lineWidth = 2.5;
  ctx.strokeRect(x - 22, y + 4, 44, 76);
  ctx.fillStyle = style.trim;
  ctx.beginPath(); ctx.arc(x, y + 40, 5, 0, Math.PI * 2); ctx.fill();
  // sign
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(x - 84, y - 78, 168, 30);
  ctx.strokeStyle = style.trim; ctx.strokeRect(x - 84, y - 78, 168, 30);
  ctx.textAlign = 'center';
  ctx.font = 'bold 10px monospace';
  ctx.fillStyle = style.trim;
  ctx.fillText(style.name, x, y - 66);
  ctx.font = 'bold 8px monospace';
  if (rental?.renter_name) {
    ctx.fillStyle = '#4ade80';
    ctx.fillText(`🏠 ${rental.renter_name}'s pad`, x, y - 54);
  } else {
    ctx.fillStyle = `rgba(255,255,255,${0.6 + Math.abs(Math.sin(t * 0.08)) * 0.4})`;
    ctx.fillText('FOR RENT · 2500 VC / 7 days', x, y - 54);
  }
}

export function drawPlanetRoom(ctx, sub, t, opts = {}) {
  const rental = opts.rental || null;
  ctx.save();
  ctx.textAlign = 'center';
  // ===================== MARS =====================
  if (sub === 'mars_surface') {
    stars(ctx, t, 45, 170);
    // twin moons
    ctx.fillStyle = '#d6d3d1';
    ctx.beginPath(); ctx.arc(120, 80, 14, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#a8a29e';
    ctx.beginPath(); ctx.arc(190, 56, 8, 0, Math.PI * 2); ctx.fill();
    // red dunes
    ctx.fillStyle = '#9a3412';
    ctx.fillRect(0, 240, 800, 280);
    ctx.fillStyle = '#7c2d12';
    ctx.beginPath(); ctx.ellipse(200, 250, 180, 26, 0, 0, Math.PI); ctx.fill();
    ctx.beginPath(); ctx.ellipse(560, 258, 150, 20, 0, 0, Math.PI); ctx.fill();
    // craters
    ctx.fillStyle = '#7c2d12';
    [[150, 430, 40, 12], [480, 470, 52, 15]].forEach(([cx, cy, rx, ry]) => {
      ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
    });
    // rover
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(180, 300, 56, 22);
    ctx.fillStyle = '#1d4ed8';
    ctx.fillRect(188, 288, 40, 12);
    ctx.fillStyle = '#111';
    [186, 206, 226].forEach((wx) => { ctx.beginPath(); ctx.arc(wx, 326, 8, 0, Math.PI * 2); ctx.fill(); });
    ctx.font = 'bold 8px monospace'; ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText('perseverance jr.', 208, 348);
    // dust devil
    const dx = 380 + Math.sin(t * 0.03) * 120;
    for (let i = 0; i < 6; i++) {
      ctx.strokeStyle = `rgba(254,215,170,${0.28 - i * 0.04})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(dx + Math.sin(t * 0.2 + i) * (4 + i * 2), 300 - i * 16, 8 + i * 4, 4, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    // alien cactus
    ctx.fillStyle = '#16a34a';
    ctx.fillRect(514, 350, 12, 44);
    ctx.fillRect(500, 360, 12, 8); ctx.fillRect(528, 368, 12, 8);
    ctx.fillStyle = '#f0abfc';
    ctx.beginPath(); ctx.arc(520, 346, 5, 0, Math.PI * 2); ctx.fill();
    // secret cave in the big crater
    const sc = PLANET_SPOTS.secret.mars;
    ctx.fillStyle = '#450a0a';
    ctx.beginPath(); ctx.ellipse(sc.x, sc.y, 34, 22, 0, Math.PI, 0); ctx.fill();
    ctx.fillStyle = '#1c0505';
    ctx.beginPath(); ctx.ellipse(sc.x, sc.y, 24, 15, 0, Math.PI, 0); ctx.fill();
    shimmer(ctx, sc.x, sc.y - 8, t, 'rgba(240,171,252,$A)');
    drawShip(ctx, PLANET_SPOTS.ship.x, PLANET_SPOTS.ship.y, 0, t, false);
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(PLANET_SPOTS.ship.x - 44, PLANET_SPOTS.ship.y - 52, 88, 14);
    ctx.fillStyle = '#7dd3fc'; ctx.fillText('🛸 back to space', PLANET_SPOTS.ship.x, PLANET_SPOTS.ship.y - 41);
    drawHouse(ctx, t, rental, { wall: '#7f1d1d', trim: '#fca5a5', name: '🔴 RED DUNE HAB' });
  } else if (sub === 'mars_house') {
    // cozy hab interior — porthole to dunes, bunk, hydro garden
    ctx.fillStyle = '#450a0a'; ctx.beginPath(); ctx.arc(400, 140, 58, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#9a3412'; ctx.beginPath(); ctx.arc(400, 158, 58, 0, Math.PI); ctx.fill();
    ctx.strokeStyle = '#fca5a5'; ctx.lineWidth = 5;
    ctx.beginPath(); ctx.arc(400, 140, 58, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#7f1d1d'; ctx.fillRect(120, 250, 170, 60);
    ctx.fillStyle = '#fecaca'; ctx.fillRect(128, 238, 62, 22);
    ctx.strokeStyle = '#fca5a5'; ctx.lineWidth = 2; ctx.strokeRect(120, 250, 170, 60);
    // hydroponic garden
    ctx.fillStyle = '#1f2937'; ctx.fillRect(560, 250, 140, 56);
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = '#16a34a';
      ctx.fillRect(572 + i * 32, 232 + Math.sin(t * 0.05 + i) * 2, 10, 20);
    }
    ctx.font = 'bold 10px monospace'; ctx.fillStyle = 'rgba(252,165,165,0.8)';
    ctx.fillText('🔴 RED DUNE HAB — home sweet Mars', 400, 60);
  } else if (sub === 'mars_secret') {
    // ===== THE MARTIAN CRYSTAL CAVERN =====
    const bg = ctx.createRadialGradient(400, 250, 40, 400, 250, 520);
    bg.addColorStop(0, '#3a0f18'); bg.addColorStop(0.5, '#1c0a0e'); bg.addColorStop(1, '#0a0406');
    ctx.fillStyle = bg; ctx.fillRect(0, 60, 800, 460);
    // ceiling stalactites (rocky spikes)
    ctx.fillStyle = '#2a0d10';
    for (let i = 0; i < 9; i++) {
      const sx = 40 + i * 92, sh = 26 + (i % 4) * 20;
      ctx.beginPath(); ctx.moveTo(sx - 16, 60); ctx.lineTo(sx + 16, 60); ctx.lineTo(sx, 60 + sh); ctx.closePath(); ctx.fill();
    }
    // rocky floor
    ctx.fillStyle = '#1a0709';
    ctx.beginPath();
    ctx.moveTo(0, 440);
    ctx.quadraticCurveTo(200, 424, 400, 436);
    ctx.quadraticCurveTo(600, 448, 800, 434);
    ctx.lineTo(800, 520); ctx.lineTo(0, 520); ctx.closePath(); ctx.fill();
    // reflective crystal pool (left)
    const pg = ctx.createLinearGradient(0, 452, 0, 500);
    pg.addColorStop(0, 'rgba(232,121,249,0.35)'); pg.addColorStop(1, 'rgba(88,28,135,0.15)');
    ctx.fillStyle = pg;
    ctx.beginPath(); ctx.ellipse(180, 480, 120, 22, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = `rgba(240,171,252,${0.4 + Math.abs(Math.sin(t * 0.06)) * 0.4})`;
    ctx.lineWidth = 1.5; ctx.stroke();
    // bioluminescent moss patches
    for (let i = 0; i < 5; i++) {
      const mx = 90 + i * 150, my = 430 + (i % 2) * 8;
      const mg = 0.4 + Math.abs(Math.sin(t * 0.05 + i)) * 0.6;
      ctx.fillStyle = `rgba(74,222,128,${mg * 0.5})`;
      ctx.beginPath(); ctx.ellipse(mx, my, 20, 5, 0, 0, Math.PI * 2); ctx.fill();
    }
    // crystal clusters (varied hues + heights) growing from the floor
    const clusters = [
      [90, 440, 22, 90, '#f0abfc'], [140, 442, 16, 60, '#c084fc'], [175, 438, 12, 42, '#f472b6'],
      [640, 440, 26, 110, '#e879f9'], [690, 444, 18, 64, '#a78bfa'], [610, 442, 12, 44, '#f0abfc'],
      [300, 448, 14, 50, '#f472b6'], [520, 448, 16, 56, '#c084fc'],
    ];
    clusters.forEach(([cx, cy, cw, ch, hue], i) => {
      const gl = 0.55 + Math.abs(Math.sin(t * 0.05 + i * 0.8)) * 0.45;
      crystal(ctx, cx, cy, cw, ch, hue, gl);
    });
    // ancient glyphs carved into the back wall, pulsing
    const glg = 0.35 + Math.abs(Math.sin(t * 0.04)) * 0.4;
    ctx.font = '20px monospace';
    ctx.fillStyle = `rgba(240,171,252,${glg})`;
    ctx.shadowColor = '#f0abfc'; ctx.shadowBlur = 8 * glg;
    '⌬ ⍟ ⌖ ⎔ ⍉ ⌘'.split(' ').forEach((g, i) => ctx.fillText(g, 190 + i * 90, 130));
    ctx.shadowBlur = 0;
    // stone pedestal + floating artifact gem
    ctx.fillStyle = '#2a1418'; ctx.fillRect(372, 300, 56, 40);
    ctx.fillStyle = '#3a1c22'; ctx.fillRect(362, 292, 76, 12);
    const flo = Math.sin(t * 0.05) * 6;
    crystal(ctx, 400, 288 + flo, 26, 60, '#e879f9', 0.7 + Math.abs(Math.sin(t * 0.05)) * 0.3);
    // giant heart crystal glowing above the pedestal
    const gg = 0.6 + Math.abs(Math.sin(t * 0.05)) * 0.4;
    ctx.save();
    ctx.shadowColor = '#f0abfc'; ctx.shadowBlur = 40 * gg;
    ctx.fillStyle = `rgba(232,121,249,${0.5 + gg * 0.4})`;
    ctx.beginPath();
    ctx.moveTo(400, 210 + flo * 0.4);
    ctx.bezierCurveTo(358, 150, 340, 200, 400, 234 + flo * 0.4);
    ctx.bezierCurveTo(460, 200, 442, 150, 400, 210 + flo * 0.4);
    ctx.closePath(); ctx.fill();
    ctx.restore();
    // light rays from the heart crystal
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + t * 0.01;
      ctx.strokeStyle = `rgba(240,171,252,${0.06 + gg * 0.06})`;
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(400, 205); ctx.lineTo(400 + Math.cos(a) * 200, 205 + Math.sin(a) * 200); ctx.stroke();
    }
    motes(ctx, t, 26, 'rgba(240,171,252,$A)', 90, 430);
    ctx.font = 'bold 11px monospace'; ctx.fillStyle = '#f0abfc'; ctx.textAlign = 'center';
    ctx.fillText('🤫 SECRET · THE MARTIAN CRYSTAL CAVERN', 400, 420);
  // ===================== VENUS =====================
  } else if (sub === 'venus_surface') {
    // golden cloud tops
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = `rgba(250,204,21,${0.1 + (i % 3) * 0.05})`;
      ctx.beginPath();
      ctx.ellipse(130 + i * 130 + Math.sin(t * 0.02 + i) * 16, 90 + (i % 2) * 40, 90, 26, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = '#a16207'; ctx.fillRect(0, 250, 800, 270);
    ctx.fillStyle = '#854d0e';
    ctx.beginPath(); ctx.ellipse(400, 252, 420, 24, 0, 0, Math.PI); ctx.fill();
    // floating platforms
    [[180, 320, 70], [420, 380, 56], [300, 450, 62]].forEach(([px, py, pr], i) => {
      const fy = py + Math.sin(t * 0.04 + i * 2) * 5;
      ctx.fillStyle = '#facc15';
      ctx.beginPath(); ctx.ellipse(px, fy, pr, 12, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(250,204,21,0.25)';
      ctx.beginPath(); ctx.ellipse(px, fy + 14, pr * 0.7, 7, 0, 0, Math.PI * 2); ctx.fill();
    });
    // glowing flora
    for (let i = 0; i < 4; i++) {
      const fx = 520 + i * 56;
      const gl = 0.5 + Math.abs(Math.sin(t * 0.07 + i)) * 0.5;
      ctx.strokeStyle = '#65a30d'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(fx, 470); ctx.quadraticCurveTo(fx + 8, 440, fx, 416); ctx.stroke();
      ctx.fillStyle = `rgba(190,242,100,${gl})`;
      ctx.beginPath(); ctx.arc(fx, 412, 6, 0, Math.PI * 2); ctx.fill();
    }
    // acid rain streaks (far right)
    for (let i = 0; i < 8; i++) {
      const ry = (t * 3 + i * 67) % 240;
      ctx.strokeStyle = 'rgba(190,242,100,0.3)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(720 + (i % 4) * 16, 40 + ry); ctx.lineTo(716 + (i % 4) * 16, 54 + ry); ctx.stroke();
    }
    // mysterious glowing door in the mist
    const sv = PLANET_SPOTS.secret.venus;
    ctx.fillStyle = 'rgba(250,204,21,0.14)';
    ctx.beginPath(); ctx.ellipse(sv.x, sv.y, 56, 40, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#422006';
    ctx.fillRect(sv.x - 18, sv.y - 34, 36, 62);
    ctx.strokeStyle = `rgba(253,224,71,${0.5 + Math.abs(Math.sin(t * 0.08)) * 0.5})`;
    ctx.lineWidth = 2.5; ctx.strokeRect(sv.x - 18, sv.y - 34, 36, 62);
    shimmer(ctx, sv.x, sv.y - 4, t, 'rgba(253,224,71,$A)');
    drawShip(ctx, PLANET_SPOTS.ship.x, PLANET_SPOTS.ship.y, 0, t, false);
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(PLANET_SPOTS.ship.x - 44, PLANET_SPOTS.ship.y - 52, 88, 14);
    ctx.fillStyle = '#7dd3fc'; ctx.fillText('🛸 back to space', PLANET_SPOTS.ship.x, PLANET_SPOTS.ship.y - 41);
    drawHouse(ctx, t, rental, { wall: '#713f12', trim: '#fde047', name: '☁️ CLOUD NINE VILLA' });
  } else if (sub === 'venus_house') {
    // sky villa — big cloud window, hammock, lemonade bar
    const wg = ctx.createLinearGradient(260, 90, 540, 210);
    wg.addColorStop(0, '#fbbf24'); wg.addColorStop(1, '#f59e0b');
    ctx.fillStyle = wg; ctx.fillRect(260, 84, 280, 130);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.beginPath(); ctx.ellipse(340, 140, 44, 14, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(470, 170, 36, 12, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#fde047'; ctx.lineWidth = 5; ctx.strokeRect(260, 84, 280, 130);
    // hammock
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(120, 250); ctx.quadraticCurveTo(200, 310, 290, 252); ctx.stroke();
    ctx.strokeStyle = '#a16207'; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(118, 320); ctx.lineTo(118, 246); ctx.moveTo(292, 320); ctx.lineTo(292, 248); ctx.stroke();
    // lemonade bar
    ctx.fillStyle = '#713f12'; ctx.fillRect(560, 250, 150, 54);
    ctx.font = '18px serif'; ctx.fillText('🍋🥤🍹', 636, 242);
    ctx.font = 'bold 10px monospace'; ctx.fillStyle = 'rgba(253,224,71,0.85)';
    ctx.fillText('☁️ CLOUD NINE VILLA — above the acid clouds', 400, 60);
  } else if (sub === 'venus_secret') {
    // ===== THE VAPORWAVE HIDEOUT =====
    const sky = ctx.createLinearGradient(0, 60, 0, 300);
    sky.addColorStop(0, '#2a0a3e'); sky.addColorStop(0.6, '#7c2d92'); sky.addColorStop(1, '#c026d3');
    ctx.fillStyle = sky; ctx.fillRect(0, 60, 800, 240);
    // twinkling stars up top
    motes(ctx, t, 30, 'rgba(255,255,255,$A)', 66, 150);
    // retro banded sun
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 60, 800, 240); ctx.clip();
    const sunG = ctx.createLinearGradient(0, 110, 0, 260);
    sunG.addColorStop(0, '#fde047'); sunG.addColorStop(0.5, '#fb7185'); sunG.addColorStop(1, '#a21caf');
    ctx.fillStyle = sunG;
    ctx.beginPath(); ctx.arc(400, 250, 96, 0, Math.PI * 2); ctx.fill();
    // scanline bands over the sun
    ctx.fillStyle = '#2a0a3e';
    for (let i = 0; i < 7; i++) ctx.fillRect(300, 210 + i * 10, 200, 4 + i);
    ctx.restore();
    // perspective neon grid floor
    ctx.fillStyle = '#150720'; ctx.fillRect(0, 300, 800, 220);
    ctx.strokeStyle = 'rgba(244,114,182,0.7)'; ctx.lineWidth = 1.5;
    for (let i = 0; i <= 12; i++) {
      const y = 300 + Math.pow(i / 12, 2) * 220;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(800, y); ctx.stroke();
    }
    for (let i = -6; i <= 6; i++) {
      ctx.beginPath(); ctx.moveTo(400, 300); ctx.lineTo(400 + i * 150, 520); ctx.stroke();
    }
    // chrome wireframe shapes floating (rotating cube feel via bob)
    const cb = Math.sin(t * 0.04) * 8;
    ctx.strokeStyle = '#67e8f9'; ctx.lineWidth = 2;
    ctx.strokeRect(120, 150 + cb, 46, 46);
    ctx.strokeRect(136, 134 + cb, 46, 46);
    ctx.beginPath();
    ctx.moveTo(120, 150 + cb); ctx.lineTo(136, 134 + cb);
    ctx.moveTo(166, 150 + cb); ctx.lineTo(182, 134 + cb);
    ctx.moveTo(120, 196 + cb); ctx.lineTo(136, 180 + cb);
    ctx.moveTo(166, 196 + cb); ctx.lineTo(182, 180 + cb);
    ctx.stroke();
    // chrome sphere (right)
    const chg = ctx.createRadialGradient(660, 160 - cb, 4, 668, 168 - cb, 34);
    chg.addColorStop(0, '#fff'); chg.addColorStop(0.5, '#c084fc'); chg.addColorStop(1, '#4c1d95');
    ctx.fillStyle = chg;
    ctx.beginPath(); ctx.arc(664, 164 - cb, 30, 0, Math.PI * 2); ctx.fill();
    // roman bust statues on pedestals
    [220, 580].forEach((bx) => {
      ctx.fillStyle = '#e5e7eb';
      ctx.fillRect(bx - 14, 360, 28, 30);           // head block
      ctx.beginPath(); ctx.arc(bx, 360, 14, Math.PI, 0); ctx.fill();
      ctx.fillStyle = '#cbd5e1'; ctx.fillRect(bx - 20, 390, 40, 14); // pedestal top
      ctx.fillRect(bx - 12, 404, 24, 40);            // column
      ctx.fillStyle = '#94a3b8'; ctx.fillRect(bx - 20, 442, 40, 10);
    });
    // palm tree silhouettes
    [70, 730].forEach((px, s) => {
      const dir = s === 0 ? 1 : -1;
      ctx.strokeStyle = '#1e0b2e'; ctx.lineWidth = 8;
      ctx.beginPath(); ctx.moveTo(px, 460); ctx.quadraticCurveTo(px + dir * 10, 380, px + dir * 4, 330); ctx.stroke();
      ctx.fillStyle = '#2a1040';
      for (let f = 0; f < 5; f++) {
        const a = -Math.PI / 2 + (f - 2) * 0.5;
        ctx.beginPath(); ctx.moveTo(px + dir * 4, 330);
        ctx.quadraticCurveTo(px + dir * 4 + Math.cos(a) * 30, 330 + Math.sin(a) * 24, px + dir * 4 + Math.cos(a) * 52, 336 + Math.sin(a) * 30);
        ctx.lineWidth = 5; ctx.strokeStyle = '#2a1040'; ctx.stroke();
      }
    });
    // neon boombox on the floor
    ctx.fillStyle = '#1e1030'; ctx.fillRect(360, 420, 80, 40);
    ctx.strokeStyle = '#f472b6'; ctx.lineWidth = 2; ctx.strokeRect(360, 420, 80, 40);
    ctx.fillStyle = '#0f0a1e';
    const bb = 0.5 + Math.abs(Math.sin(t * 0.2)) * 0.5;
    [378, 422].forEach((sx) => {
      ctx.beginPath(); ctx.arc(sx, 440, 11, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = `rgba(103,232,249,${bb})`; ctx.beginPath(); ctx.arc(sx, 440, 7, 0, Math.PI * 2); ctx.stroke();
    });
    // neon "HIDEOUT" sign
    const ng = 0.6 + Math.abs(Math.sin(t * 0.09)) * 0.4;
    ctx.font = 'bold 30px monospace'; ctx.textAlign = 'center';
    ctx.shadowColor = '#22d3ee'; ctx.shadowBlur = 18 * ng;
    ctx.fillStyle = `rgba(103,232,249,${0.7 + ng * 0.3})`;
    ctx.fillText('H I D E O U T', 400, 110);
    ctx.shadowBlur = 0;
    ctx.font = 'bold 11px monospace'; ctx.fillStyle = '#f9a8d4';
    ctx.fillText('🤫 SECRET · THE VAPORWAVE HIDEOUT', 400, 500);
  // ===================== SATURN =====================
  } else if (sub === 'saturn_surface') {
    stars(ctx, t, 55, 150);
    // giant saturn horizon
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath(); ctx.arc(660, 40, 120, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(251,191,36,0.5)'; ctx.lineWidth = 14;
    ctx.beginPath(); ctx.ellipse(660, 40, 200, 46, -0.3, 0, Math.PI * 2); ctx.stroke();
    // ring-ice ground
    ctx.fillStyle = '#cffafe'; ctx.fillRect(0, 250, 800, 270);
    ctx.fillStyle = 'rgba(103,232,249,0.4)';
    ctx.beginPath(); ctx.ellipse(400, 252, 420, 22, 0, 0, Math.PI); ctx.fill();
    // drifting ice chunks
    for (let i = 0; i < 5; i++) {
      const ix = 120 + i * 150 + Math.sin(t * 0.02 + i * 2) * 10;
      ctx.fillStyle = 'rgba(224,242,254,0.9)';
      ctx.beginPath();
      ctx.moveTo(ix, 300 + (i % 2) * 90);
      ctx.lineTo(ix + 26, 310 + (i % 2) * 90);
      ctx.lineTo(ix + 18, 330 + (i % 2) * 90);
      ctx.lineTo(ix - 10, 324 + (i % 2) * 90);
      ctx.closePath(); ctx.fill();
    }
    // ice geyser
    const gh = Math.abs(Math.sin(t * 0.05)) * 70;
    ctx.strokeStyle = 'rgba(165,243,252,0.7)'; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(210, 460); ctx.lineTo(210, 460 - gh); ctx.stroke();
    ctx.fillStyle = 'rgba(224,242,254,0.8)';
    ctx.beginPath(); ctx.arc(210, 456 - gh, 10 + gh * 0.1, 0, Math.PI * 2); ctx.fill();
    // ice crystals
    for (let i = 0; i < 4; i++) {
      const cx = 480 + i * 60;
      const gl = 0.5 + Math.abs(Math.sin(t * 0.06 + i)) * 0.5;
      ctx.fillStyle = `rgba(103,232,249,${gl})`;
      ctx.beginPath(); ctx.moveTo(cx, 470); ctx.lineTo(cx - 8, 440); ctx.lineTo(cx, 414); ctx.lineTo(cx + 8, 440); ctx.closePath(); ctx.fill();
    }
    // secret hatch in the ice
    const ss = PLANET_SPOTS.secret.saturn;
    ctx.fillStyle = '#164e63';
    ctx.beginPath(); ctx.ellipse(ss.x, ss.y, 30, 16, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = `rgba(103,232,249,${0.5 + Math.abs(Math.sin(t * 0.08)) * 0.5})`;
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.ellipse(ss.x, ss.y, 30, 16, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#0e7490';
    ctx.beginPath(); ctx.arc(ss.x + 18, ss.y, 3.5, 0, Math.PI * 2); ctx.fill();
    shimmer(ctx, ss.x, ss.y - 10, t, 'rgba(103,232,249,$A)');
    drawShip(ctx, PLANET_SPOTS.ship.x, PLANET_SPOTS.ship.y, 0, t, false);
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(PLANET_SPOTS.ship.x - 44, PLANET_SPOTS.ship.y - 52, 88, 14);
    ctx.fillStyle = '#7dd3fc'; ctx.fillText('🛸 back to space', PLANET_SPOTS.ship.x, PLANET_SPOTS.ship.y - 41);
    drawHouse(ctx, t, rental, { wall: '#155e75', trim: '#67e8f9', name: '🪐 RING VIEW LOFT' });
  } else if (sub === 'saturn_house') {
    // loft with panorama ring window + telescope + ice bar
    ctx.fillStyle = '#020617'; ctx.fillRect(180, 80, 440, 140);
    stars(ctx, t, 25, 130);
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath(); ctx.arc(400, 150, 40, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(251,191,36,0.6)'; ctx.lineWidth = 7;
    ctx.beginPath(); ctx.ellipse(400, 150, 72, 18, -0.3, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = '#67e8f9'; ctx.lineWidth = 5; ctx.strokeRect(180, 80, 440, 140);
    // ice bar
    ctx.fillStyle = 'rgba(165,243,252,0.35)'; ctx.fillRect(130, 260, 160, 48);
    ctx.strokeStyle = '#67e8f9'; ctx.lineWidth = 2; ctx.strokeRect(130, 260, 160, 48);
    ctx.font = '16px serif'; ctx.fillText('🧊🍸🧊', 210, 252);
    // telescope
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 10;
    ctx.beginPath(); ctx.moveTo(590, 300); ctx.lineTo(650, 230); ctx.stroke();
    ctx.fillStyle = '#334155'; ctx.fillRect(566, 296, 60, 18);
    ctx.font = 'bold 10px monospace'; ctx.fillStyle = 'rgba(103,232,249,0.85)';
    ctx.fillText('🪐 RING VIEW LOFT — best view in the system', 400, 56);
  } else if (sub === 'saturn_secret') {
    // ===== THE FROZEN TREASURE VAULT =====
    const ice = ctx.createLinearGradient(0, 60, 0, 520);
    ice.addColorStop(0, '#041b2e'); ice.addColorStop(0.55, '#062436'); ice.addColorStop(1, '#0a3145');
    ctx.fillStyle = ice; ctx.fillRect(0, 60, 800, 460);
    // aurora ribbons overhead
    for (let i = 0; i < 4; i++) {
      const ag = ctx.createLinearGradient(0, 70, 800, 190);
      ag.addColorStop(0, `hsla(${(t * 1.4 + i * 70) % 360},85%,62%,0.2)`);
      ag.addColorStop(1, 'transparent');
      ctx.fillStyle = ag;
      ctx.beginPath();
      ctx.moveTo(0, 84 + i * 24);
      ctx.quadraticCurveTo(400, 54 + i * 24 + Math.sin(t * 0.03 + i) * 22, 800, 96 + i * 24);
      ctx.lineTo(800, 150 + i * 24);
      ctx.quadraticCurveTo(400, 120 + i * 24, 0, 150 + i * 24);
      ctx.closePath(); ctx.fill();
    }
    // icicle stalactites from the ceiling
    ctx.fillStyle = 'rgba(165,243,252,0.55)';
    for (let i = 0; i < 11; i++) {
      const sx = 30 + i * 74, sh = 24 + (i % 3) * 22;
      ctx.beginPath(); ctx.moveTo(sx - 8, 60); ctx.lineTo(sx + 8, 60); ctx.lineTo(sx, 60 + sh); ctx.closePath(); ctx.fill();
    }
    // ice pillars / columns (back)
    [110, 690, 250, 560].forEach((px, i) => {
      const pw = i < 2 ? 26 : 18;
      const cg = ctx.createLinearGradient(px - pw, 0, px + pw, 0);
      cg.addColorStop(0, 'rgba(103,232,249,0.15)'); cg.addColorStop(0.5, 'rgba(207,250,254,0.55)'); cg.addColorStop(1, 'rgba(103,232,249,0.15)');
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.moveTo(px - pw, 430); ctx.lineTo(px - pw * 0.5, 120); ctx.lineTo(px + pw * 0.5, 120); ctx.lineTo(px + pw, 430);
      ctx.closePath(); ctx.fill();
    });
    // reflective ice floor
    const fg = ctx.createLinearGradient(0, 430, 0, 520);
    fg.addColorStop(0, 'rgba(165,243,252,0.4)'); fg.addColorStop(1, 'rgba(8,49,69,0.2)');
    ctx.fillStyle = fg; ctx.fillRect(0, 430, 800, 90);
    // frozen artifacts encased in translucent ice blocks
    [[150, 400, '⚔️'], [640, 400, '🏺']].forEach(([bx, by, icon]) => {
      ctx.fillStyle = 'rgba(207,250,254,0.28)';
      ctx.fillRect(bx - 26, by - 44, 52, 66);
      ctx.strokeStyle = 'rgba(165,243,252,0.7)'; ctx.lineWidth = 1.5;
      ctx.strokeRect(bx - 26, by - 44, 52, 66);
      ctx.font = '24px serif'; ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(icon, bx, by - 4);
      ctx.fillStyle = 'rgba(255,255,255,0.25)'; // ice glare streak
      ctx.beginPath(); ctx.moveTo(bx - 20, by - 40); ctx.lineTo(bx - 8, by - 40); ctx.lineTo(bx - 20, by + 4); ctx.closePath(); ctx.fill();
    });
    // treasure hoard: overflowing chest
    const tg = 0.6 + Math.abs(Math.sin(t * 0.07)) * 0.4;
    // golden glow halo
    ctx.save();
    ctx.shadowColor = '#fde047'; ctx.shadowBlur = 34 * tg;
    ctx.fillStyle = `rgba(253,224,71,${tg * 0.28})`;
    ctx.beginPath(); ctx.ellipse(400, 320, 130, 60, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    // coin pile
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath(); ctx.ellipse(400, 356, 110, 26, 0, 0, Math.PI * 2); ctx.fill();
    for (let i = 0; i < 22; i++) {
      const a = (i * 999) % 360, r = (i * 37) % 100;
      const cx = 400 + Math.cos(a) * r, cy = 344 + Math.sin(a) * (r * 0.24);
      ctx.fillStyle = i % 2 ? '#fcd34d' : '#f59e0b';
      ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2); ctx.fill();
    }
    // the chest
    ctx.fillStyle = '#78350f'; ctx.fillRect(346, 296, 108, 56);
    ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 3; ctx.strokeRect(346, 296, 108, 56);
    ctx.fillStyle = '#92400e';
    ctx.beginPath(); ctx.ellipse(400, 296, 54, 22, 0, Math.PI, 0); ctx.fill();
    ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.ellipse(400, 296, 54, 22, 0, Math.PI, 0); ctx.stroke();
    ctx.fillStyle = '#fde047'; ctx.fillRect(392, 316, 16, 14); // lock
    // gems + crown spilling out
    ctx.font = '22px serif'; ctx.textAlign = 'center';
    ctx.fillText('👑', 400, 292);
    ctx.font = '18px serif';
    ctx.fillText('💎', 356, 348); ctx.fillText('💠', 446, 350); ctx.fillText('🪙', 400, 372);
    // frost / snow particles drifting down
    motes(ctx, t, 34, 'rgba(207,250,254,$A)', 70, 500);
    ctx.font = 'bold 11px monospace'; ctx.fillStyle = '#67e8f9'; ctx.textAlign = 'center';
    ctx.fillText('🤫 SECRET · THE FROZEN TREASURE VAULT', 400, 420);
  }
  ctx.restore();
}
